import { z } from 'zod'

/**
 * EC（体験チケット購入・決済）ドメインのスキーマ。
 *
 * バックエンド EC 機能拡張（#3258 / Stripe Connect・Destination Charges）の API 契約に対応する。
 * レスポンスキーは lowerCamelCase。値の詳細は #3258 設計書および `/api/v2/doc` を参照。
 */

// ─────────────────────────────────────────────
// Enum
// ─────────────────────────────────────────────
/** 商品種別（#3281 で TICKET / COURSE_UNLOCK / MERCHANDISE に整理） */
export const ProductTypeEnum = z.enum(['TICKET', 'COURSE_UNLOCK', 'MERCHANDISE'])
export type ProductType = z.infer<typeof ProductTypeEnum>

/** チケット利用種別（ON_REDEMPTION=後日消込 / ON_PURCHASE=購入即消込） */
export const TicketUsageTypeEnum = z.enum(['ON_REDEMPTION', 'ON_PURCHASE'])
export type TicketUsageType = z.infer<typeof TicketUsageTypeEnum>

export const OrderStatusEnum = z.enum(['PENDING', 'COMPLETED', 'FAILED', 'REFUNDED', 'PARTIALLY_REFUNDED'])
export type OrderStatus = z.infer<typeof OrderStatusEnum>

export const UserAssetStatusEnum = z.enum(['ACTIVE', 'USED', 'EXPIRED', 'REVOKED'])
export type UserAssetStatus = z.infer<typeof UserAssetStatusEnum>

// ─────────────────────────────────────────────
// 商品一覧（GET /api/v2/products）— #3281 で外税・税額を返却
// 未認証でも取得可（前提購入なし商品のみ）。#3383 で購入状態（purchased / purchasable）も返る。
// ─────────────────────────────────────────────
export const ProductListItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  productType: ProductTypeEnum,
  /** 商品説明 */
  description: z.string().nullable(),
  /** 販売金額（円・税込 = 単価 + 消費税額）。決済はこの金額を請求 */
  salesAmount: z.number(),
  /** 単価（円・税抜） */
  unitPrice: z.number(),
  /** 消費税額（円 = 単価 × 消費税率 ÷ 100 の切り捨て） */
  taxAmount: z.number(),
  /** 消費税率（%整数、0=内税） */
  taxRate: z.number(),
  currency: z.string(),
  /** 残在庫（null=無制限） */
  stock: z.number().nullable(),
  /** 商品画像URL（presigned） */
  imageUrl: z.string().nullable(),
  salesStartAt: z.string().nullable(),
  salesEndAt: z.string().nullable(),
  /**
   * 購入済みか（有効／使用済の権利を保有）。未認証は常に false。
   * 旧バックエンド（フィールド未返却）では false にフォールバックし、購入済み表示を行わない。
   */
  purchased: z.boolean().default(false),
  /**
   * いま購入できるか（購入条件・在庫をサーバが評価した結果）。
   * TICKET / COURSE_UNLOCK は購入済みなら false、MERCHANDISE は再購入可のため在庫次第。
   * 旧バックエンド（フィールド未返却）では true にフォールバックし、従来どおり購入ボタンを活性にする。
   */
  purchasable: z.boolean().default(true),
})
export type ProductListItem = z.infer<typeof ProductListItemSchema>

export const ProductListResponseSchema = z.object({
  products: z.array(ProductListItemSchema),
})
export type ProductListResponse = z.infer<typeof ProductListResponseSchema>

// ─────────────────────────────────────────────
// 商品詳細（GET /api/v2/products/{id}）— 認証必須。購入可否を含む
// ─────────────────────────────────────────────
export const ProductDetailSchema = ProductListItemSchema.extend({
  ticketUsageType: TicketUsageTypeEnum,
  /** 購入可否（購入条件・在庫・販売期間を評価した結果） */
  purchasable: z.boolean(),
  /** 購入不可の理由 */
  reason: z.string().nullable(),
})
export type ProductDetail = z.infer<typeof ProductDetailSchema>

/** GET /api/v2/products クエリパラメータ（商品種別フィルター） */
export type ListProductsParams = {
  productType?: ProductType
}

// ─────────────────────────────────────────────
// Checkout セッション作成（POST /api/v2/ticket/checkout/sessions）
// ─────────────────────────────────────────────
export const CheckoutSessionResponseSchema = z.object({
  /** Stripe ホストの Checkout ページ URL（リダイレクト先） */
  checkoutUrl: z.string(),
  orderId: z.number(),
})
export type CheckoutSessionResponse = z.infer<typeof CheckoutSessionResponseSchema>

export type CreateCheckoutSessionRequest = {
  productId: number
  quantity?: number
  successUrl?: string
  cancelUrl?: string
}

// ─────────────────────────────────────────────
// マイ注文・保有チケット（GET /api/v2/ticket/orders）
// ─────────────────────────────────────────────
export const UserAssetSchema = z.object({
  userAssetId: z.number(),
  /** COURSE_ACCESS / TICKET / MERCHANDISE */
  assetType: z.string(),
  /** 対象マスタID（開放コース/シートID等） */
  referenceId: z.number().nullable(),
  /** 消込用コード */
  code: z.string().nullable(),
  status: UserAssetStatusEnum,
  expiresAt: z.string().nullable(),
  /** 消込時刻 */
  usedAt: z.string().nullable(),
})
export type UserAsset = z.infer<typeof UserAssetSchema>

export const TicketOrderSchema = z.object({
  orderId: z.number(),
  /** Stripe PaymentIntent ID（未作成の場合は null） */
  paymentIntentId: z.string().nullable(),
  productName: z.string(),
  /** 合計金額（円） */
  amount: z.number(),
  currency: z.string(),
  status: OrderStatusEnum,
  /** 累計返金額（円） */
  amountRefunded: z.number(),
  purchasedAt: z.string().nullable(),
  /** 返金可否（サーバ判定：キャンペーントグル × 返金ポリシー） */
  refundable: z.boolean(),
  assets: z.array(UserAssetSchema),
})
export type TicketOrder = z.infer<typeof TicketOrderSchema>

export const TicketOrderListResponseSchema = z.object({
  orders: z.array(TicketOrderSchema),
})
export type TicketOrderListResponse = z.infer<typeof TicketOrderListResponseSchema>

/** 決済明細（商品情報スナップショット）1件 */
export const CheckoutPaymentItemSchema = z.object({
  productId: z.number().nullable(),
  /** 購入時商品名スナップショット */
  productName: z.string(),
  quantity: z.number(),
  /** 購入時単価（円・スナップショット） */
  price: z.number(),
  appliedPaymentMethod: z.string(),
})
export type CheckoutPaymentItem = z.infer<typeof CheckoutPaymentItemSchema>

/** Stripe Checkout Session ID をキーにした決済詳細（GET /api/v2/ticket/checkout/sessions/{sessionId}） */
export const CheckoutPaymentDetailSchema = z.object({
  sessionId: z.string(),
  orderId: z.number(),
  paymentStatus: OrderStatusEnum,
  paymentIntentId: z.string().nullable(),
  chargeId: z.string().nullable(),
  amount: z.number(),
  currency: z.string(),
  amountRefunded: z.number(),
  paidAt: z.string().nullable(),
  items: z.array(CheckoutPaymentItemSchema),
})
export type CheckoutPaymentDetail = z.infer<typeof CheckoutPaymentDetailSchema>
